// #region IMPORTS
import type { Material, Object3D } from 'three';

// #endregion

// #region TRAVERSAL

export function traverseFind<T extends Object3D>(
  root: Object3D,
  findAll: boolean = true,
  fn = (o: Object3D) => o.type === 'SkinnedMesh',
): Array<T> {
  const stack: Array<Object3D> = [root];
  const rtn: Array<T> = [];

  let o: Object3D | undefined;

  while (stack.length > 0) {
    o = stack.pop();
    if (o) {
      if (fn(o)) {
        rtn.push(o as T);
        if (!findAll) return rtn; // only return the first one by user request
      }

      // Add childen to the stack to test them too
      stack.push(...o.children);
    }
  }

  return rtn;
}

export function revTraverseFind<T extends Object3D>(
  root: Object3D,
  findAll: boolean = true,
  fn: ((o: Object3D) => boolean) | null = null,
): Array<T> {
  const rtn: Array<T> = [];
  let obj = root;

  while (obj.parent) {
    obj = obj.parent;
    if (!fn || fn(obj)) {
      rtn.push(obj as T);
      if (!findAll) return rtn;
    }
  }

  return rtn;
}

// #endregion

// #region COLOR

export function glColor(
  hex: number,
  out: Array<number> = [0, 0, 0],
): Array<number> {
  const NORMALIZE_RGB = 1 / 255;
  out[0] = ((hex >> 16) & 255) * NORMALIZE_RGB;
  out[1] = ((hex >> 8) & 255) * NORMALIZE_RGB;
  out[2] = (hex & 255) * NORMALIZE_RGB;
  return out;
}

// #endregion

// #region CLEAN UP

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function disposeObject3D(obj: any) {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Remove from parent & dispose its 3D resources
  if (obj.parent) {
    obj.parent.remove(obj);
  }

  obj?.geometry?.dispose();
  if (obj.material) disposeMaterial(obj.material);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Take care of any of the children items if available
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj.traverse((o: any) => {
    o?.geometry?.dispose();
    if (o.material) disposeMaterial(o.material);
  });
}

export function disposeMaterial(mat: Material) {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Looking for textures in regular materials
  for (const v of Object.values(mat)) {
    if (v?.dispose && typeof v.dispose === 'function') {
      v.dispose();
    }
  }

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Looking for textures in ShaderMaterials, RawShaderMaterials
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const o of Object.values<any>((mat as any).uniforms)) {
    if (o.value.dispose) o.value.dispose();
  }

  mat.dispose();
}

// #endregion
